import { useState } from 'react'
import { Button, Modal, Select, TextInput } from '@mantine/core'
import { USDateInput } from '@/components/ui/USDateInput'
import { IconAlertTriangle } from '@tabler/icons-react'
import type { Patient } from '@medplum/fhirtypes'
import type { TefcaRetrySearchCandidate } from '@behuman/core-contract'
import { Controller, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useTefcaRetrySearch } from '@/hooks/useTefcaRetrySearch'
import { useTefcaManualLink } from '@/hooks/useTefcaManualLink'
import { useTefcaManualCreate } from '@/hooks/useTefcaManualCreate'

/** The open Match Review Task's reason, as parsed off the Task by the badge. */
export type TefcaOpenReview = {
  reason: string
  missingFields: string[]
}

const MISSING_FIELD_LABELS: Record<string, string> = {
  'first-name': 'First Name',
  'last-name': 'Last Name',
  'birth-date': 'Date of Birth',
  gender: 'Gender',
  'zip-code': 'ZIP Code',
}

type KeyDemographics = {
  firstName: string
  lastName: string
  birthDate: string
  gender: string
  zipCode: string
}

function chartKeyDemographics(patient: Patient): KeyDemographics {
  return {
    firstName: patient.name?.[0]?.given?.[0] ?? '',
    lastName: patient.name?.[0]?.family ?? '',
    birthDate: patient.birthDate ?? '',
    gender: patient.gender ?? '',
    zipCode: patient.address?.[0]?.postalCode ?? '',
  }
}

const CANDIDATE_ROWS: {
  label: string
  chartField: keyof KeyDemographics
  candidateField: keyof TefcaRetrySearchCandidate
}[] = [
  { label: 'First Name', chartField: 'firstName', candidateField: 'givenName' },
  { label: 'Last Name', chartField: 'lastName', candidateField: 'familyName' },
  { label: 'Date of Birth', chartField: 'birthDate', candidateField: 'birthDate' },
  { label: 'Gender', chartField: 'gender', candidateField: 'gender' },
  { label: 'ZIP Code', chartField: 'zipCode', candidateField: 'postalCode' },
]

// Case-insensitive on purpose: the highlight guides an identity judgement
// ("is this my patient?"), where "SMITH" vs "Smith" is noise, not a difference.
const differsFromChart = (chartValue: string, candidateValue: string): boolean =>
  chartValue.trim().toLowerCase() !== candidateValue.trim().toLowerCase()

const formatUsDate = (value: string): string => {
  const [year, month, day] = value.split('-')
  return year && month && day ? `${month}/${day}/${year}` : value
}

const CandidateCard = ({
  candidate,
  chart,
}: {
  candidate: TefcaRetrySearchCandidate
  chart: KeyDemographics
}) => (
  <table className="w-full border-collapse text-sm">
    <thead>
      <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
        <th className="py-1 pr-2 font-medium">Field</th>
        <th className="py-1 pr-2 font-medium">Chart</th>
        <th className="py-1 font-medium">TEFCA Candidate</th>
      </tr>
    </thead>
    <tbody>
      {CANDIDATE_ROWS.map(({ label, chartField, candidateField }) => {
        const chartValue = chart[chartField]
        const candidateValue = candidate[candidateField]
        const differs = differsFromChart(chartValue, candidateValue)
        const isDate = chartField === 'birthDate'
        const chartDisplay = isDate ? formatUsDate(chartValue) : chartValue
        const candidateDisplay = isDate ? formatUsDate(candidateValue) : candidateValue
        return (
          <tr key={label} className="border-b border-gray-100 last:border-b-0">
            <td className="py-1 pr-2 text-gray-500">{label}</td>
            <td className="py-1 pr-2">{chartDisplay}</td>
            <td className="py-1">
              <span
                className={
                  differs ? 'rounded bg-orange-100 px-1 font-semibold text-orange-800' : undefined
                }
              >
                {candidateDisplay}
              </span>
              {differs && <span className="sr-only"> (differs from chart)</span>}
            </td>
          </tr>
        )
      })}
    </tbody>
  </table>
)

type Props = {
  patient: Patient
  review: TefcaOpenReview
  opened: boolean
  onClose: () => void
}

/**
 * Match Review retry screen: the five Key Demographics pre-filled from the
 * chart as ephemeral, search-only values — edits never touch the Patient
 * resource. Retrying runs the exact TEFCA search with the typed values;
 * candidates render side-by-side with the chart demographics (display-only).
 *
 * For an `incomplete-demographics` review there is nothing meaningful to
 * retry: the search (and the Create action) is disabled, the missing Key
 * Demographics are named, and an inline link points to the chart's
 * demographics editor — fixing the chart re-fires the sync automatically.
 */
export const TefcaMatchReviewModal = ({ patient, review, opened, onClose }: Props) => {
  const chart = chartKeyDemographics(patient)
  const form = useForm<KeyDemographics>({ defaultValues: chart })
  const search = useTefcaRetrySearch()
  const link = useTefcaManualLink({ onSuccess: onClose })
  const [confirmCreateOpened, setConfirmCreateOpened] = useState(false)
  const create = useTefcaManualCreate({
    onSuccess: () => {
      setConfirmCreateOpened(false)
      onClose()
    },
  })

  const fields = form.watch()
  const hasBlankField = Object.values(fields).some((value) => !value?.trim())

  // Create is an action on the chart's data: unsaved search edits are never
  // sent, and their presence triggers an explicit warning in the dialog.
  const formDeviatesFromChart = (Object.keys(chart) as (keyof KeyDemographics)[]).some((key) =>
    differsFromChart(chart[key], fields[key] ?? ''),
  )

  const retrySearch = (values: KeyDemographics): void => {
    search.mutate({
      givenName: values.firstName,
      familyName: values.lastName,
      birthDate: values.birthDate,
      gender: values.gender,
      postalCode: values.zipCode,
    })
  }

  const candidates = search.data?.candidates

  const searchDisabled = review.reason === 'incomplete-demographics'
  const missingFieldLabels = review.missingFields.map(
    (field) => MISSING_FIELD_LABELS[field] ?? field,
  )

  return (
    <Modal opened={opened} onClose={onClose} title="TEFCA Match Review" size="lg">
      {review.reason === 'no-match' && (
        <div className="mb-4 flex gap-2 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          <IconAlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <h3 className="font-semibold">No TEFCA records found</h3>
            <p className="mt-1">
              Search completed with 0 records. Retry using corrected search demographics below.
              These edits are used only for TEFCA search and do not overwrite the BeHuman chart.
            </p>
          </div>
        </div>
      )}
      {searchDisabled && (
        <div className="mb-4 rounded border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900">
          TEFCA search is disabled: the chart is missing Key Demographics —{' '}
          <span className="font-semibold">{missingFieldLabels.join(', ')}</span>. Add the missing{' '}
          {missingFieldLabels.length === 1 ? 'field' : 'fields'} in the{' '}
          <Link
            to={`/patients/${patient.id}/demographics`}
            onClick={onClose}
            className="font-semibold underline"
          >
            chart&apos;s demographics editor
          </Link>{' '}
          — the TEFCA search re-runs automatically once the chart is complete.
        </div>
      )}
      <form onSubmit={form.handleSubmit(retrySearch)}>
        <div className="grid grid-cols-2 gap-3">
          <Controller
            name="firstName"
            control={form.control}
            render={({ field }) => (
              <TextInput label="First Name" disabled={searchDisabled} {...field} />
            )}
          />
          <Controller
            name="lastName"
            control={form.control}
            render={({ field }) => (
              <TextInput label="Last Name" disabled={searchDisabled} {...field} />
            )}
          />
          <Controller
            name="birthDate"
            control={form.control}
            render={({ field }) => (
              <USDateInput
                label="Date of Birth"
                disabled={searchDisabled}
                {...field}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                maxDate={new Date()}
              />
            )}
          />
          <Controller
            name="gender"
            control={form.control}
            render={({ field }) => (
              <Select
                label="Gender"
                data={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                  { value: 'unknown', label: 'Unknown' },
                ]}
                disabled={searchDisabled}
                {...field}
                onChange={(value) => field.onChange(value ?? '')}
              />
            )}
          />
          <Controller
            name="zipCode"
            control={form.control}
            render={({ field }) => (
              <TextInput label="ZIP Code" disabled={searchDisabled} {...field} />
            )}
          />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button
            type="submit"
            loading={search.isPending}
            disabled={hasBlankField || searchDisabled}
          >
            Retry TEFCA Search
          </Button>
          <Button
            type="button"
            variant="subtle"
            disabled={searchDisabled}
            onClick={() => form.reset(chart)}
          >
            Reset to Chart Data
          </Button>
        </div>
      </form>
      {candidates && candidates.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          {candidates.map((candidate) => {
            const isLinking = (updateChart: boolean): boolean =>
              link.isPending &&
              link.variables?.hgPatientId === candidate.hgPatientId &&
              link.variables?.updateChart === updateChart
            return (
              <div key={candidate.hgPatientId} className="rounded border border-gray-200 p-3">
                <CandidateCard candidate={candidate} chart={chart} />
                <div className="mt-3 flex gap-3">
                  <Button
                    size="xs"
                    loading={isLinking(false)}
                    disabled={link.isPending}
                    onClick={() =>
                      link.mutate({
                        patientId: patient.id as string,
                        hgPatientId: candidate.hgPatientId,
                        updateChart: false,
                      })
                    }
                  >
                    Link this patient
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    loading={isLinking(true)}
                    disabled={link.isPending}
                    onClick={() =>
                      link.mutate({
                        patientId: patient.id as string,
                        hgPatientId: candidate.hgPatientId,
                        updateChart: true,
                      })
                    }
                  >
                    Link & update chart
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {candidates && candidates.length === 0 && (
        <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          No TEFCA records found for these values. You can adjust the fields and retry the search.
        </div>
      )}
      {!searchDisabled && (
        <div
          className={
            candidates?.length === 0
              ? 'mt-4 rounded border border-red-300 bg-red-50 p-3'
              : 'mt-4 rounded border border-gray-200 p-3'
          }
        >
          <h3 className="text-sm font-semibold text-red-700">Create New TEFCA Patient Record</h3>
          {candidates?.length === 0 && (
            <p className="mt-1 text-sm font-medium text-red-700">
              If the chart data is correct, this patient may not exist in TEFCA yet.
            </p>
          )}
          <p className="mt-1 text-sm text-gray-600">
            If the chart data is confirmed correct and the search truly finds no TEFCA record, you
            can create one. Creating a record registers this patient in the network — it does not
            mean outside records exist, and the import may come back empty.
          </p>
          <Button
            className="mt-3"
            size="xs"
            color="red"
            variant={candidates?.length === 0 ? 'filled' : 'outline'}
            disabled={create.isPending}
            onClick={() => setConfirmCreateOpened(true)}
          >
            Create New TEFCA Patient Record
          </Button>
        </div>
      )}
      <Modal
        opened={confirmCreateOpened}
        onClose={() => setConfirmCreateOpened(false)}
        title="Confirm TEFCA Record Creation"
      >
        <p className="text-sm">You are about to create a new TEFCA patient record for:</p>
        <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
          <div className="font-semibold">
            {chart.firstName} {chart.lastName}
          </div>
          <div className="text-gray-600">
            Born {formatUsDate(chart.birthDate)} · {chart.gender} · ZIP {chart.zipCode}
          </div>
        </div>
        <p className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          If this patient already exists in TEFCA under different demographics, creating a new
          record will produce a duplicate in a national network. Only create after confirming the
          chart data is correct and the search truly found no match.
        </p>
        {formDeviatesFromChart && (
          <p className="mt-3 rounded border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
            Your search form has unsaved edits that differ from the chart. Creation always uses the
            chart&apos;s data verbatim — the search edits will not be used.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="subtle" onClick={() => setConfirmCreateOpened(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={create.isPending}
            onClick={() => create.mutate({ patientId: patient.id as string })}
          >
            Create TEFCA Record
          </Button>
        </div>
      </Modal>
    </Modal>
  )
}
